import { useEffect } from "react";
import { LatestUpdatesPanel } from "@/components/dashboard/LatestUpdatesPanel";
import { GatedSection, UpgradeOverlay } from "@/components/dashboard/UpgradeGate";
import { DocumentUploadGate } from "@/components/dashboard/DocumentUploadGate";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";

import { useAuth } from "@/contexts/AuthContext";
import { usePageMeta } from "@/hooks/usePageMeta";

import { BrokerGettingStarted } from "@/components/broker-dashboard/BrokerGettingStarted";
import { BrokerHeroStats } from "@/components/broker-dashboard/BrokerHeroStats";
import { BrokerActionItemsPanel } from "@/components/broker-dashboard/BrokerActionItemsPanel";
import { BrokerBidsBoard } from "@/components/broker-dashboard/BrokerBidsBoard";
import { BrokerInsightsGrid } from "@/components/broker-dashboard/BrokerInsightsGrid";
import { BrokerQuickActions } from "@/components/broker-dashboard/BrokerQuickActions";
import { BrokerRecentActivityFeed } from "@/components/broker-dashboard/BrokerRecentActivityFeed";
import { BrokerTeamActivityShowcase } from "@/components/broker-dashboard/BrokerTeamActivityShowcase";


/**
 * Broker LR Direct Dashboard — parallel to ShipperDashboardV2.
 * Route: /lr-direct/broker
 *
 * This is the LR Direct command center for brokers (find, bid, win, dispatch).
 * It is DISTINCT from /broker-dashboard which is the load-board command center.
 */
export default function BrokerLRDirectDashboardV2() {
  usePageMeta({
    title: "Broker Dashboard | LR Direct",
    description: "LR Direct broker command center",
  });
  const { user, profile } = useAuth();
  const firstName = profile?.first_name || "there";

  useEffect(() => {
    const main = document.querySelector("main");
    if (main) main.scrollTop = 0;
  }, []);

  const userId = user?.id || "";

  // Legacy dashboard blocks hidden pending redesign. The components and all of
  // their data logic/hooks are intentionally kept intact below — flip this
  // flag (or re-wire the sections) to restore them.
  const SHOW_LEGACY_BLOCKS = false;

  if (!profile) {
    return (
      <div className="flex flex-col gap-3 xl:gap-4 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 xl:gap-4">
          <div className="rounded-lg border bg-card p-4 animate-pulse">
            <div className="h-3 w-32 bg-muted rounded mb-3" />
            <div className="h-5 w-64 bg-muted rounded mb-2" />
            <div className="h-3 w-full max-w-md bg-muted rounded" />
          </div>
          <div className="rounded-lg border bg-card p-4 h-24 animate-pulse" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="rounded-lg border bg-card p-4 h-24 animate-pulse" />
          ))}
        </div>
        <div className="rounded-lg border bg-card p-4 h-64 animate-pulse" />
      </div>
    );
  }

  return (
    <>
      <UpgradeOverlay />
      <DocumentUploadGate />
      <div className="flex flex-col gap-3 xl:gap-4 w-full">
        {/* Welcome strip */}
        <GatedSection>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 xl:gap-4">
            <div className="rounded-lg border bg-card p-4">
              <p className="text-[10px] font-bold uppercase tracking-widest text-brand-teal-deep">
                Broker Dashboard · LR Direct
              </p>
              <h2 className="mt-1 text-lg font-bold text-foreground truncate">
                Welcome back, {firstName}
              </h2>
              <p className="mt-1 text-caption text-muted-foreground">
                Find direct shipper freight, place bids, win loads, and run every dispatch in one place.
              </p>
            </div>
            <LatestUpdatesPanel />
          </div>
        </GatedSection>

        <ErrorBoundary variant="section" label="Getting Started">
          <GatedSection>
            <BrokerGettingStarted />
          </GatedSection>
        </ErrorBoundary>

        {SHOW_LEGACY_BLOCKS && (
        <ErrorBoundary variant="section" label="Hero Stats">
          <GatedSection>
            <BrokerHeroStats userId={userId} />
          </GatedSection>
        </ErrorBoundary>
        )}

        {SHOW_LEGACY_BLOCKS && (
        <ErrorBoundary variant="section" label="Action Items">
          <GatedSection>
            <BrokerActionItemsPanel userId={userId} />
          </GatedSection>
        </ErrorBoundary>
        )}

        {SHOW_LEGACY_BLOCKS && (
        <ErrorBoundary variant="section" label="My Bids">
          <GatedSection>
            <BrokerBidsBoard userId={userId} />
          </GatedSection>
        </ErrorBoundary>
        )}

        {SHOW_LEGACY_BLOCKS && (
        <ErrorBoundary variant="section" label="Insights">
          <GatedSection>
            <BrokerInsightsGrid userId={userId} />
          </GatedSection>
        </ErrorBoundary>
        )}

        {SHOW_LEGACY_BLOCKS && (
        <ErrorBoundary variant="section" label="Quick Actions">
          <GatedSection>
            <BrokerQuickActions />
          </GatedSection>
        </ErrorBoundary>
        )}

        {SHOW_LEGACY_BLOCKS && (
        <ErrorBoundary variant="section" label="Recent Activity">
          <GatedSection>
            <BrokerRecentActivityFeed userId={userId} />
          </GatedSection>
        </ErrorBoundary>
        )}

        {/* ── Team Activity (visual clone of approved mockup — sample data only) ── */}
        <ErrorBoundary variant="section" label="Team Activity">
          <GatedSection>
            <BrokerTeamActivityShowcase />
          </GatedSection>
        </ErrorBoundary>
      </div>
    </>
  );
}
